import { Trans, useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import cx from 'clsx';
import { IconRcDefaultNft, IconDefaultToken } from 'assets/icon/token';
import { IconRcGas } from '@/assets/history';
import { LazyMedia, ProjectAvatar } from 'components';
import {
  numSeparate,
  shortenAddress,
  HistoryGetTimeAgo,
  CHAINS_DICT,
  openNewWindow,
  getTokenSymbol,
  formatNetworth,
} from '@/utils';
import { TokenHistoryItem, ContentScope, CexDetail } from '../../types';

import style from './index.module.scss';
import { isEmpty, truncate } from 'lodash';

interface Project {
  id: string;
  logo_url: string;
  name: string;
  is_deposit?: boolean;
}
interface HistoryRowProps {
  cancel?: Function;
  currentNonce?: number;
  info: TokenHistoryItem;
  projects: Record<string, Project>;
  cexs: Record<string, CexDetail>;
  memos: Record<string, string>;
  cate: {
    [index: string]: {
      name: string;
      id: string;
    };
  };
  tokens: Record<string, Tokens>;
  chain: string;
  onTokenClick?(arg: any): void;
  scope: ContentScope;
}

const TokenChange = ({
  changes,
  tokens,
  chain,
  amountSymbol,
}: Pick<HistoryRowProps, 'tokens' | 'chain'> & {
  onTokenClick?(arg: any): void;
  changes: TokenHistoryItem['receives'];
  amountSymbol?: '-' | '+';
}) => {
  return (
    <>
      {changes.length
        ? changes.map((v, i) => {
            const token = tokens[v.token_id];
            const isNft = v.token_id?.length === 32;
            const symbol = getTokenSymbol(token);
            const name = isNft
              ? token?.name || (symbol ? `${symbol} ${token?.inner_id}` : 'Unknown NFT')
              : symbol;
            let displayName = name;
            const getTokenChangeText = () =>
              `${isNft ? v.amount : numSeparate(v.amount, 4, true)} ${displayName}`;
            const originTokenChangeText = getTokenChangeText();
            const overflowLength = originTokenChangeText.length - 35;
            const isOverflow = overflowLength > 0;

            if (isOverflow) {
              displayName = `${name.slice(0, Math.max(3, name.length - overflowLength))}...`;
            }

            const tokenChangeText = getTokenChangeText();
            const price = v?.price || token?.price || 0;

            return (
              <div
                key={v.token_id}
                className={cx(
                  style.tokenChangeItem,
                  amountSymbol === '+' && style.success,
                  isNft && style.isNft,
                )}
                data-id={v.token_id}
                data-name={name}
                // title={isOverflow ? name : undefined}
                data-token-chain={chain}>
                <div className={style.tokenIcon}>
                  {isNft ? (
                    <LazyMedia
                      failedPlaceholder={<IconRcDefaultNft />}
                      src={token?.content}
                      type={token?.content_type}
                      className={style.changeTokenIcon}
                    />
                  ) : (
                    <LazyMedia
                      failedPlaceholder={<img src={IconDefaultToken} alt="" />}
                      src={token?.logo_url}
                      className={cx(style.changeTokenIcon, style.isToken)}
                    />
                  )}
                </div>
                <span className={cx(style.tokenChangeText, isNft && style.isNft)}>
                  <span
                    style={{
                      width: 8,
                      display: 'inline-block',
                      textAlign: 'center',
                    }}>
                    {amountSymbol}
                  </span>
                  <span className={style.tokenSymbol}>{tokenChangeText}</span>
                  {isNft ? null : `（${formatNetworth(price * v.amount)}）`}
                </span>
              </div>
            );
          })
        : null}
    </>
  );
};

const HistoryRow = (props: HistoryRowProps) => {
  const { t } = useTranslation();

  const { info, projects, cexs, cate, tokens, memos, onTokenClick } = props;
  const isFailed = useMemo(() => info.tx?.status === 0, [info.tx?.status]);
  const isScam = useMemo(() => info?.is_scam, [info?.is_scam]);
  const isCancel = useMemo(() => info.cate_id === 'cancel', [info.cate_id]);
  const isApprove = useMemo(() => info.cate_id === 'approve', [info.cate_id]);
  const isDisplayCex = useMemo(() => {
    if (!cexs[info.other_addr]) return false;
    const isSend = !isEmpty(info.sends);
    const isReceive = !isEmpty(info.receives);
    const isDeposit = cexs[info.other_addr]?.is_deposit;
    return (isDeposit && isSend) || (!isDeposit && isReceive);
  }, [cexs, info.other_addr, info.receives, info.sends]);
  const isDisplayMemo = useMemo(() => {
    return (info.cate_id === 'send' || info.cate_id === 'receive') && memos[info.other_addr];
  }, [info.cate_id, info.other_addr, memos]);

  const project = useMemo(() => {
    if (isDisplayCex) return cexs[info.other_addr];
    return projects[info.project_id];
  }, [isDisplayCex, cexs, info.other_addr, info.project_id, projects]);

  const sinceTime = useMemo(
    () =>
      Date.now() / 1000 - info.time_at < 3600 * 24
        ? `${HistoryGetTimeAgo(info.time_at)} ago`
        : dayjs(info.time_at * 1000).format('YYYY/MM/DD HH:mm:ss'),
    [info.time_at],
  );

  const projectName = useMemo(() => {
    const name = project?.name ?? shortenAddress(info.other_addr || '');
    return (
      <span
        className={style.greyText}
        onClick={() => openNewWindow(`/profile/${info.other_addr}`, '_blank', true)}>
        {name}
        {isDisplayMemo ? ` (${truncate(memos[info.other_addr], { length: 20 })})` : null}
      </span>
    );
  }, [info.other_addr, project?.name, isDisplayMemo, memos]);

  const typeName = useMemo(() => {
    if (isDisplayCex) {
      const isDeposit = project?.is_deposit;
      const isSend = !isEmpty(info.sends);
      const isReceive = !isEmpty(info.receives);
      if (isDeposit && isSend) {
        return t('MobileRow.deposit', { name: project?.name });
      }
      if (!isDeposit && isReceive) {
        return t('MobileRow.withdraw', { name: project?.name });
      }
    }
    if (isCancel) {
      return t('MobileRow.cancelTx');
    }

    if (isApprove) {
      const approveToken = tokens[info.token_approve?.token_id || ''];
      const amount = info?.token_approve?.value || 0;

      return (
        <Trans
          i18nKey="TransactionAction.approveDesc"
          values={{
            amount: amount < 1e9 ? amount.toFixed(4) : t('TransactionAction.infinite'),
            token: approveToken?.display_symbol || approveToken?.symbol,
          }}
          components={{ projectName }}
        />
      );
    }

    return cate[info.cate_id || '']?.name ?? (info.tx?.name || t('MobileRow.contract'));
  }, [
    t,
    isDisplayCex,
    isCancel,
    isApprove,
    cate,
    info.cate_id,
    info.tx?.name,
    info.sends,
    info.receives,
    info.token_approve?.token_id,
    info.token_approve?.value,
    project?.is_deposit,
    project?.name,
    tokens,
    projectName,
  ]);

  const gasText = useMemo(() => {
    if (!info?.tx?.eth_gas_fee) {
      return '';
    }

    let str = numSeparate(info.tx.eth_gas_fee, 4);

    if (info.chain && CHAINS_DICT[info.chain]?.nativeTokenSymbol) {
      str += ` ${CHAINS_DICT[info.chain]?.nativeTokenSymbol}`;
    }

    if (info.tx?.usd_gas_fee) {
      str += ` (${formatNetworth(info.tx?.usd_gas_fee)})`;
    }

    return str;
  }, [info.chain, info?.tx?.eth_gas_fee, info?.tx?.usd_gas_fee]);

  return (
    <div className={cx(style.view, (isFailed || isScam) && style.isFailed)}>
      <div className={style.header}>
        <ProjectAvatar url={project?.logo_url} type={info.cate_id} chain={info.chain} size={32} />
        <div className={style.projectInfo}>
          <div className={style.type}>
            <span className={style.opType}>{typeName}</span>
            {isFailed ? <span className={style.failed}>{t('MobileRow.yoexct')}</span> : null}
            {isScam ? <span className={style.scam}>{t('MobileRow.1j3q4')}</span> : null}
          </div>
          {!isCancel && !isApprove ? <div className={style.addr}>{projectName}</div> : null}
        </div>
      </div>
      {info.sends?.length || info.receives?.length ? (
        <div className={style.tokens}>
          <TokenChange
            amountSymbol="-"
            changes={info.sends}
            tokens={tokens}
            chain={info.chain}
            onTokenClick={onTokenClick}
          />
          <TokenChange
            amountSymbol="+"
            changes={info.receives}
            tokens={tokens}
            chain={info.chain}
            onTokenClick={onTokenClick}
          />
        </div>
      ) : null}
      <div className={style.footer}>
        <span className={style.time}>{sinceTime}</span>
        {gasText ? (
          <div className={style.gasWrap}>
            <IconRcGas />
            <span className={style.gasText}>{gasText}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default HistoryRow;
